import React from 'react';
import Icon from '../../../components/AppIcon';

const TrustedClients = () => {
  // Split clients into two tiers for marquee animation
  const tier1Clients = [
    {
      id: 1,
      name: "TelecomCore Networks",
      industry: "Telecommunications",
      outcome: "99.9% uptime achieved",
      logo: "TC"
    },
    {
      id: 2,
      name: "GlobalConnect Systems",
      industry: "Network Equipment",
      outcome: "40% improved signal clarity",
      logo: "GC"
    },
    {
      id: 3,
      name: "WirelessTech Solutions",
      industry: "Wireless Infrastructure",
      outcome: "60% coverage expansion",
      logo: "WT"
    },
    {
      id: 4,
      name: "DataFlow Networks",
      industry: "Data Centers",
      outcome: "50% latency reduction",
      logo: "DF"
    },
    {
      id: 5,
      name: "SatelliteLink Corp",
      industry: "Satellite Communications",
      outcome: "35% signal strength boost",
      logo: "SL"
    },
    {
      id: 6,
      name: "FiberOptic Dynamics",
      industry: "Fiber Optics",
      outcome: "10x bandwidth increase",
      logo: "FO"
    },
  ];

  const tier2Clients = [
    {
      id: 7,
      name: "NetworkPro Solutions",
      industry: "Enterprise Networks",
      outcome: "75% efficiency boost",
      logo: "NP"
    },
    {
      id: 8,
      name: "CloudLink Technologies",
      industry: "Cloud Infrastructure",
      outcome: "90% cost reduction",
      logo: "CL"
    },
    {
      id: 9,
      name: "SecureComm Systems",
      industry: "Security Solutions",
      outcome: "100% threat prevention",
      logo: "SC"
    },
    {
      id: 10,
      name: "MobileWave Corp",
      industry: "Mobile Networks",
      outcome: "200% coverage increase",
      logo: "MW"
    },
    {
      id: 11,
      name: "QuantumNet Industries",
      industry: "Quantum Communications",
      outcome: "Ultra-secure transmission",
      logo: "QN"
    },
    {
      id: 12,
      name: "EdgeTech Networks",
      industry: "Edge Computing",
      outcome: "80% latency reduction",
      logo: "ET"
    },
  ];

  // Component for individual client card in marquee
  const ClientCard = ({ client, tier }) => (
    <div className={`flex-shrink-0 mx-4 group ${tier === 1 ? 'w-80' : 'w-72'}`}>
      <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl border border-slate-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:bg-white">
        <div className="flex items-center space-x-4 mb-4">
          <div className={`${tier === 1 ? 'w-14 h-14' : 'w-12 h-12'} rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md`}>
            <span className="text-white font-bold text-lg">
              {client.logo}
            </span>
          </div>
          <div className="flex-1">
            <h3 className={`${tier === 1 ? 'text-lg' : 'text-base'} font-semibold text-slate-800 mb-1`}>
              {client.name}
            </h3>
            <p className="text-sm text-slate-600">
              {client.industry}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={16} className="text-green-500 flex-shrink-0" />
          <span className={`${tier === 1 ? 'text-sm' : 'text-xs'} font-medium text-slate-700`}>
            {client.outcome}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 font-heading mb-4">
            Trusted by <span className="text-blue-600">Telecommunications Leaders</span>
          </h2>
          <p className="text-xl text-slate-600 font-body max-w-3xl mx-auto">
            We supply precision-engineered components to telecommunications companies worldwide, powering the networks that connect our digital world
          </p>
        </div>
      </div>

      {/* Two-Tier Marquee Animation */}
      <div className="space-y-8">
        {/* First Tier - Moving Right */}
        <div className="relative">
          <div className="flex animate-marquee-right">
            {/* First set of clients */}
            {tier1Clients.map((client) => (
              <ClientCard key={client.id} client={client} tier={1} />
            ))}
            {/* Duplicate set for seamless loop */}
            {tier1Clients.map((client) => (
              <ClientCard key={`${client.id}-duplicate`} client={client} tier={1} />
            ))}
          </div>
        </div>

        {/* Second Tier - Moving Left */}
        <div className="relative">
          <div className="flex animate-marquee-left">
            {/* First set of clients */}
            {tier2Clients.map((client) => (
              <ClientCard key={client.id} client={client} tier={2} />
            ))}
            {/* Duplicate set for seamless loop */}
            {tier2Clients.map((client) => (
              <ClientCard key={`${client.id}-duplicate`} client={client} tier={2} />
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-blue-600 text-3xl font-bold font-heading mb-2">500+</div>
              <p className="text-slate-600 font-body">Components Delivered</p>
            </div>
            <div>
              <div className="text-cyan-600 text-3xl font-bold font-heading mb-2">99.9%</div>
              <p className="text-slate-600 font-body">Network Reliability</p>
            </div>
            <div>
              <div className="text-indigo-600 text-3xl font-bold font-heading mb-2">50+</div>
              <p className="text-slate-600 font-body">Telecom Partners</p>
            </div>
            <div>
              <div className="text-purple-600 text-3xl font-bold font-heading mb-2">10+</div>
              <p className="text-slate-600 font-body">Years in Telecom</p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for marquee animations */}
      <style jsx>{`
        @keyframes marquee-right {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(0%);
          }
        }

        @keyframes marquee-left {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-100%);
          }
        }

        .animate-marquee-right {
          animation: marquee-right 30s linear infinite;
        }

        .animate-marquee-left {
          animation: marquee-left 35s linear infinite;
        }

        /* Pause animation on hover */
        .animate-marquee-right:hover,
        .animate-marquee-left:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export default TrustedClients;